import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding,
  faCar,
  faChevronRight,
  faCrosshairs,
  faExpand,
  faGlobe,
  faLocationDot,
  faMagnifyingGlass,
  faTriangleExclamation,
  faUser,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { httpsCallable } from 'firebase/functions';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { deleteObject, ref, uploadBytes } from 'firebase/storage';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth, firestore, functions, storage } from '../firebaseConfig';
import { getOrCreateDirectConversation } from '../firebase/messagingUtils';
import { STATES } from '../utils/states';
import { useUser } from '../context/UserContext';
import PageInfoButton from '../components/PageInfoButton';
import './Fieldbook.css';

const TYPE_OPTIONS = ['person', 'vehicle', 'place'];

const SCOPE_ICONS = {
  organization: faBuilding,
  global: faGlobe,
};

const FACE_SEARCH_MESSAGES = {
  FACE_NOT_FOUND: 'No usable face was detected in this image. Try a clearer photo with the face fully visible.',
  MULTIPLE_FACES: 'More than one face was detected. Use an image containing only the person you want to search.',
  INVALID_IMAGE: 'This image could not be read. Try a supported JPG or PNG image.',
  NO_MATCHES: 'No possible face matches found.',
  FACE_SEARCH_UNAVAILABLE: 'The face search is temporarily unavailable. Please try again.',
  FACE_SEARCH_FAILED: 'The face search could not be completed. Please try again.',
};

const TYPE_ICONS = {
  person: faUser,
  vehicle: faCar,
  place: faLocationDot,
};

const SEARCH_OPTIONS = {
  person: [
    {
      value: 'firstLast',
      label: 'First and last name',
      fields: [
        { name: 'firstName', label: 'First name', autoComplete: 'given-name' },
        { name: 'lastName', label: 'Last name', autoComplete: 'family-name' },
      ],
    },
    {
      value: 'firstLastDob',
      label: 'First, last, and DOB',
      fields: [
        { name: 'firstName', label: 'First name', autoComplete: 'given-name' },
        { name: 'lastName', label: 'Last name', autoComplete: 'family-name' },
        { name: 'dob', label: 'Date of birth', type: 'date' },
      ],
    },
    {
      value: 'lastDob',
      label: 'Last name and DOB',
      fields: [
        { name: 'lastName', label: 'Last name', autoComplete: 'family-name' },
        { name: 'dob', label: 'Date of birth', type: 'date' },
      ],
    },
    {
      value: 'dlState',
      label: 'Driver license and state',
      fields: [
        { name: 'dl', label: 'Driver license' },
        { name: 'dlState', label: 'DL state', control: 'state' },
      ],
    },
    {
      value: 'addressCityState',
      label: 'Address, city, and state',
      fields: [
        { name: 'streetAddress', label: 'Street address', autoComplete: 'street-address' },
        { name: 'city', label: 'City', autoComplete: 'address-level2' },
        { name: 'addressState', label: 'State', autoComplete: 'address-level1' },
      ],
    },
    {
      value: 'phone',
      label: 'Phone number',
      fields: [
        { name: 'phone', label: 'Phone number', type: 'tel', autoComplete: 'tel' },
      ],
    },
  ],
  vehicle: [
    {
      value: 'plateState',
      label: 'Plate and state',
      fields: [
        { name: 'plate', label: 'License plate' },
        { name: 'plateState', label: 'Plate state', maxLength: 30 },
      ],
    },
  ],
  place: [
    {
      value: 'addressState',
      label: 'Address and state',
      fields: [
        { name: 'streetAddress', label: 'Street address', autoComplete: 'street-address' },
        { name: 'addressState', label: 'State', autoComplete: 'address-level1' },
      ],
    },
  ],
};

const labelFor = (value) => value.charAt(0).toUpperCase() + value.slice(1);
export const formatPublicFaceSimilarity = (value) => Number.isFinite(Number(value))
  ? `${Number(value).toFixed(1)}% face match`
  : '';
// Officer-facing public-record federation is separately released from
// administrator-controlled acquisition. It remains off unless a production
// build explicitly enables it.
export const publicRecordFederationEnabled = () =>
  process.env.REACT_APP_PUBLIC_RECORDS_FEDERATION_ENABLED === 'true';
// Public-record face search is available in the existing Global face-search
// experience. Structured-name federation remains separately configured.
const PUBLIC_RECORD_FACE_FEDERATION_ENABLED = true;
const isPublicStructuredSearch = (scope, type, searchMode) =>
  publicRecordFederationEnabled() && scope === 'global' && type === 'person' &&
  ['firstLast', 'firstLastDob'].includes(searchMode);
const visibleFieldbookResults = (results, { allowPublic = false } = {}) =>
  (Array.isArray(results) ? results : [])
    .filter((result) => allowPublic || result?.resultType !== 'public_record');

export const fieldbookFaceSearchMessage = (value = {}) => {
  const details = value?.details && typeof value.details === 'object' ? value.details : {};
  const code = value?.faceSearchCode || details.faceSearchCode || '';
  if (FACE_SEARCH_MESSAGES[code]) return FACE_SEARCH_MESSAGES[code];
  if (value?.code === 'functions/unavailable' || value?.code === 'unavailable') {
    return FACE_SEARCH_MESSAGES.FACE_SEARCH_UNAVAILABLE;
  }
  return FACE_SEARCH_MESSAGES.FACE_SEARCH_FAILED;
};

const STATE_CODE_BY_NAME = {
  Alabama: 'AL', Alaska: 'AK', Arizona: 'AZ', Arkansas: 'AR', California: 'CA',
  Colorado: 'CO', Connecticut: 'CT', Delaware: 'DE', Florida: 'FL', Georgia: 'GA',
  Hawaii: 'HI', Idaho: 'ID', Illinois: 'IL', Indiana: 'IN', Iowa: 'IA', Kansas: 'KS',
  Kentucky: 'KY', Louisiana: 'LA', Maine: 'ME', Maryland: 'MD', Massachusetts: 'MA',
  Michigan: 'MI', Minnesota: 'MN', Mississippi: 'MS', Missouri: 'MO', Montana: 'MT',
  Nebraska: 'NE', Nevada: 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
  'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND',
  Ohio: 'OH', Oklahoma: 'OK', Oregon: 'OR', Pennsylvania: 'PA', 'Rhode Island': 'RI',
  'South Carolina': 'SC', 'South Dakota': 'SD', Tennessee: 'TN', Texas: 'TX', Utah: 'UT',
  Vermont: 'VT', Virginia: 'VA', Washington: 'WA', 'Washington, D.C.': 'DC',
  'West Virginia': 'WV', Wisconsin: 'WI', Wyoming: 'WY',
};
const STATE_NAME_BY_CODE = Object.fromEntries(
  Object.entries(STATE_CODE_BY_NAME).map(([name, code]) => [code, name]),
);
export const normalizePublicRecordState = (value) => {
  const text = String(value || '').trim();
  if (/^[A-Za-z]{2}$/.test(text)) return text.toUpperCase();
  return STATE_CODE_BY_NAME[text] || '';
};
const stateLabel = (stateCode) => STATE_NAME_BY_CODE[stateCode] || stateCode;

const formatDate = (value) => {
  if (!value) return '';
  const seconds = value.seconds ?? value._seconds;
  const date = typeof value.toDate === 'function'
    ? value.toDate()
    : Number.isFinite(Number(seconds))
      ? new Date(Number(seconds) * 1000)
      : new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
};
const formatSourceRecordDate = (value) =>
  /^\d{4}-\d{2}-\d{2}$/.test(String(value || '')) ? value : formatDate(value);
const DOB_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(?:T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,9})?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;
export const formatDateOfBirth = (value) => {
  const raw = typeof value === 'string' ? value.trim() : '';
  if (!raw) return '';
  const match = DOB_DATE_PATTERN.exec(raw);
  if (!match) return raw;
  const [, yearText, monthText, dayText] = match;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const calendarDate = new Date(Date.UTC(year, month - 1, day));
  if (calendarDate.getUTCFullYear() !== year || calendarDate.getUTCMonth() !== month - 1 ||
      calendarDate.getUTCDate() !== day) return raw;
  return `${monthText}/${dayText}/${yearText}`;
};

const calculateAge = (dob) => {
  if (!dob) return '';
  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) return '';
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDifference = today.getMonth() - birth.getMonth();
  if (
    monthDifference < 0 ||
    (monthDifference === 0 && today.getDate() < birth.getDate())
  ) {
    age--;
  }
  return age >= 0 ? age : '';
};

const InteractionImagePreview = ({ file, onRemove }) => {
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  return (
    <div className="fieldbook-selected-file">
      {previewUrl && <img src={previewUrl} alt={`Selected ${file.name}`} />}
      <button
        type="button"
        className="fieldbook-remove-selected-file"
        aria-label={`Remove ${file.name}`}
        title="Remove photo"
        onClick={onRemove}
      >
        X
      </button>
      <span title={file.name}>{file.name}</span>
    </div>
  );
};

export default function Fieldbook() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser = null } = useUser() || {};
  const entryPathRef = useRef(location.pathname);
  const [fieldbookAcknowledged, setFieldbookAcknowledged] = useState(false);
  const [scope, setScope] = useState(null);
  const [type, setType] = useState('person');
  const [searchMode, setSearchMode] = useState('firstLast');
  const [criteria, setCriteria] = useState({});
  const [publicRecordState, setPublicRecordState] = useState('');
  const [publicRecordStates, setPublicRecordStates] = useState([]);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [adminOrganizationState, setAdminOrganizationState] = useState('');
  const [adminOrganizationId, setAdminOrganizationId] = useState('');
  const [adminOrganizations, setAdminOrganizations] = useState([]);
  const [requestId, setRequestId] = useState('');
  const [results, setResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [selected, setSelected] = useState(null);
  const [interactions, setInteractions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isOpening, setIsOpening] = useState('');
  const [isFaceSearching, setIsFaceSearching] = useState(false);
  const [hasFaceSearchResults, setHasFaceSearchResults] = useState(false);
  const [faceSearchPickerOpen, setFaceSearchPickerOpen] = useState(false);
  const [faceSearchPreview, setFaceSearchPreview] = useState('');
  const [enlargedReferencePhoto, setEnlargedReferencePhoto] = useState(null);
  const [deletingInteractionId, setDeletingInteractionId] = useState('');
  const [launchingDmUserId, setLaunchingDmUserId] = useState('');
  const [interactionFormOpen, setInteractionFormOpen] = useState(false);
  const [interactionDraft, setInteractionDraft] = useState({ narrative: '' });
  const [interactionFiles, setInteractionFiles] = useState([]);
  const [isSavingInteraction, setIsSavingInteraction] = useState(false);
  const [mergePickerOpen, setMergePickerOpen] = useState(false);
  const [mergeSourceRecordId, setMergeSourceRecordId] = useState('');
  const [isMerging, setIsMerging] = useState(false);
  const [message, setMessage] = useState('');

  const resetScopeSelection = useCallback(() => {
    if (faceSearchPreview?.startsWith('blob:')) {
      URL.revokeObjectURL(faceSearchPreview);
    }
    setScope(null);
    setType('person');
    setSearchMode(SEARCH_OPTIONS.person[0].value);
    setCriteria({});
    setPublicRecordState('');
    setResults([]);
    setHasFaceSearchResults(false);
    setHasSearched(false);
    setRequestId('');
    setSelected(null);
    setInteractions([]);
    setMessage('');
    setFaceSearchPickerOpen(false);
    setFaceSearchPreview('');
    setEnlargedReferencePhoto(null);
  }, [faceSearchPreview]);

  useLayoutEffect(() => {
    if (entryPathRef.current === location.pathname) return;
    entryPathRef.current = location.pathname;
    setFieldbookAcknowledged(false);
    resetScopeSelection();
  }, [location.pathname, resetScopeSelection]);

  useEffect(() => {
    if (!selected) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setSelected(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selected]);

  useEffect(() => {
    let cancelled = false;
    const loadAdminClaim = async () => {
      try {
        const token = await auth.currentUser?.getIdTokenResult?.();
        if (!cancelled) setIsPlatformAdmin(token?.claims?.admin === true);
      } catch (_) {
        if (!cancelled) setIsPlatformAdmin(false);
      }
    };
    loadAdminClaim();
    return () => {
      cancelled = true;
    };
  }, [currentUser?.uid]);

  useEffect(() => {
    if (scope !== 'organization' || !isPlatformAdmin) {
      setAdminOrganizations([]);
      setAdminOrganizationState('');
      setAdminOrganizationId('');
      return undefined;
    }
    let cancelled = false;
    const loadOrganizations = async () => {
      try {
        const snapshot = await getDocs(collection(firestore, 'organizations'));
        const organizations = snapshot.docs.map((item) => {
          const data = item.data() || {};
          return {
            id: item.id,
            name: String(data.name || '').trim(),
            stateCode: normalizePublicRecordState(data.stateCode || data.state),
            status: data.status || '',
          };
        }).filter((item) => item.status === 'active' && item.name && item.stateCode)
          .sort((left, right) => left.name.localeCompare(right.name));
        if (!cancelled) setAdminOrganizations(organizations);
      } catch (_) {
        if (!cancelled) setAdminOrganizations([]);
      }
    };
    loadOrganizations();
    return () => {
      cancelled = true;
    };
  }, [isPlatformAdmin, scope]);

  useEffect(() => {
    if (!isPublicStructuredSearch(scope, type, searchMode)) {
      setPublicRecordStates([]);
      setPublicRecordState('');
      return undefined;
    }
    let cancelled = false;
    const loadStates = async () => {
      try {
        const readStates = httpsCallable(functions, 'getFieldbookPublicRecordStructuredStates');
        const result = await readStates();
        const states = Array.from(new Set((Array.isArray(result.data?.states) ? result.data.states : [])
          .map(normalizePublicRecordState).filter(Boolean))).sort();
        if (!cancelled) {
          setPublicRecordStates(states);
          const profileState = normalizePublicRecordState(currentUser?.state);
          setPublicRecordState((current) => states.includes(current) ? current :
            (states.includes(profileState) ? profileState : ''));
        }
      } catch (_) {
        if (!cancelled) setPublicRecordStates([]);
      }
    };
    loadStates();
    return () => {
      cancelled = true;
    };
  }, [currentUser?.state, scope, type, searchMode]);

  const adminOrganizationStates = Array.from(new Set(adminOrganizations
    .map((organization) => organization.stateCode))).sort();
  const visibleAdminOrganizations = adminOrganizations
    .filter((organization) => organization.stateCode === adminOrganizationState);

  const makeClientId = (prefix) => {
    const randomPart =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}_${Math.random().toString(36).slice(2)}`;
    return `${prefix}_${randomPart}`.replace(/[^A-Za-z0-9_-]/g, '_');
  };

  const uploadFaceSearchTempImage = async (file) => {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error('Sign in required.');
    const draftId = makeClientId('fieldbook_search');
    const safeName = String(file.name || 'face-search.jpg')
      .replace(/[^A-Za-z0-9_.-]/g, '_')
      .slice(0, 80);
    const path = `personTemp/${uid}/${draftId}/fieldbook_${Date.now()}_${safeName}`;
    const fileRef = ref(storage, path);
    const snapshot = await uploadBytes(fileRef, file);
    return snapshot.ref.fullPath;
  };

  const runSearch = async (event) => {
    event?.preventDefault();
    const selectedSearch = SEARCH_OPTIONS[type]
      .find((option) => option.value === searchMode);
    if (!selectedSearch || selectedSearch.fields.some(
      (field) => !String(criteria[field.name] || '').trim()
    )) {
      setMessage('Complete every field for the selected search.');
      return;
    }
    if (scope === 'organization' && isPlatformAdmin &&
        (!adminOrganizationState || !adminOrganizationId)) {
      setMessage('Select a State and organization for Organization search.');
      return;
    }
    setIsSearching(true);
    setHasFaceSearchResults(false);
    setMessage('');
    setSelected(null);
    setInteractions([]);
    try {
      const callableName = scope === 'organization'
        ? 'searchOrganizationFieldbook'
        : 'searchGlobalFieldbook';
      const search = httpsCallable(functions, callableName);
      const result = await search({ type, searchMode, criteria,
        ...(isPublicStructuredSearch(scope, type, searchMode) && publicRecordState
          ? {publicRecordState} : {}),
        ...(scope === 'organization' && isPlatformAdmin ? {
          organizationId: adminOrganizationId,
          organizationState: adminOrganizationState,
        } : {}), });
      const visibleResults = visibleFieldbookResults(result.data?.results, {
        allowPublic: publicRecordFederationEnabled(),
      });
      setRequestId(result.data?.requestId || '');
      setResults(visibleResults);
      setHasSearched(true);
      if (!visibleResults.length) {
        setMessage('No matching Fieldbook records found.');
      }
    } catch (err) {
      console.error('Fieldbook search failed:', err);
      setMessage(err?.message || 'Fieldbook search failed.');
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const openResult = async (recordId) => {
    if (!requestId || !recordId) return;
    if (!publicRecordFederationEnabled() && !PUBLIC_RECORD_FACE_FEDERATION_ENABLED &&
        String(recordId).startsWith('fr_')) {
      setMessage('Public-record federation is not available.');
      return;
    }
    setIsOpening(recordId);
    setMessage('');
    setEnlargedReferencePhoto(null);
    setInteractionFormOpen(false);
    setInteractionFiles([]);
    setMergePickerOpen(false);
    setMergeSourceRecordId('');
    try {
      const open = httpsCallable(functions, 'openFieldbookResult');
      const result = await open({ requestId, recordId });
      setSelected(result.data?.record || null);
      setInteractions(result.data?.interactions || []);
    } catch (err) {
      console.error('Unable to open Fieldbook result:', err);
      setMessage(err?.message || 'Unable to open this Fieldbook record.');
    } finally {
      setIsOpening('');
    }
  };

  const renewPublicReferencePhoto = async () => {
    if (!publicRecordFederationEnabled() && !PUBLIC_RECORD_FACE_FEDERATION_ENABLED) return;
    if (!requestId || selected?.resultType !== 'public_record' || !selected?.id) return;
    if (selected.referencePhotos?.[0]?.renewalAttempted) return;
    const renewingRecordId = selected.id;
    setSelected((current) => current?.id === renewingRecordId ? {
      ...current,
      referencePhotos: current.referencePhotos?.map((photo, index) =>
        index === 0 ? { ...photo, renewalAttempted: true } : photo),
    } : current);
    try {
      const renew = httpsCallable(functions, 'renewFieldbookPublicRecordPhoto');
      const result = await renew({ requestId, recordId: selected.id });
      const signedUrl = result.data?.url;
      if (!signedUrl) throw new Error('Photo renewal was unavailable.');
      setSelected((current) => current?.id === renewingRecordId ? {
        ...current,
        referencePhotos: [{
          signedUrl,
          expiresAt: result.data?.expiresAt || '',
          renewalAttempted: true,
        }],
      } : current);
    } catch (err) {
      setMessage(err?.message || 'Unable to renew this reference photo.');
    }
  };

  const handleScopeChange = (nextScope) => {
    if (!fieldbookAcknowledged) return;
    setScope(nextScope);
    setResults([]);
    setHasFaceSearchResults(false);
    setSelected(null);
    setInteractions([]);
    setRequestId('');
    setHasSearched(false);
    setMessage('');
    setEnlargedReferencePhoto(null);
    if (nextScope === 'global' && type === 'place') {
      setType('person');
      setSearchMode(SEARCH_OPTIONS.person[0].value);
      setCriteria({});
    }
  };

  const returnToScopeSelection = () => {
    resetScopeSelection();
  };

  const handleTypeChange = (nextType) => {
    setType(nextType);
    setSearchMode(SEARCH_OPTIONS[nextType][0].value);
    setCriteria({});
    setResults([]);
    setHasFaceSearchResults(false);
    setSelected(null);
    setInteractions([]);
    setRequestId('');
    setHasSearched(false);
    setMessage('');
    setEnlargedReferencePhoto(null);
  };

  const handleFaceSearchSelect = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (scope === 'organization' && isPlatformAdmin &&
        (!adminOrganizationState || !adminOrganizationId)) {
      setMessage('Select a State and organization for Organization face search.');
      return;
    }
    if (type !== 'person') setType('person');
    if (faceSearchPreview?.startsWith('blob:')) URL.revokeObjectURL(faceSearchPreview);
    const preview = URL.createObjectURL(file);
    setFaceSearchPreview(preview);
    setHasFaceSearchResults(false);
    setIsFaceSearching(true);
    setFaceSearchPickerOpen(false);
    setSelected(null);
    setInteractions([]);
    setMessage('');
    try {
      const imagePath = await uploadFaceSearchTempImage(file);
      const search = httpsCallable(functions, 'searchFieldbookFaces');
      const result = await search({ scope, imagePath,
        ...(scope === 'organization' && isPlatformAdmin ? {
          organizationId: adminOrganizationId,
          organizationState: adminOrganizationState,
        } : {}), });
      setRequestId(result.data?.requestId || '');
      const visibleResults = visibleFieldbookResults(result.data?.results, {
        allowPublic: scope === 'global' && PUBLIC_RECORD_FACE_FEDERATION_ENABLED,
      });
      setResults(visibleResults);
      setHasFaceSearchResults(true);
      setHasSearched(true);
      setMessage(result.data?.faceSearchCode ? fieldbookFaceSearchMessage(result.data) :
        (result.data?.partialResults ? 'Some face-search sources are temporarily unavailable. Results may be incomplete.'
          : visibleResults.length ? '' : FACE_SEARCH_MESSAGES.NO_MATCHES));
    } catch (err) {
      console.error('Fieldbook face search failed:', err);
      setResults([]);
      setHasFaceSearchResults(false);
      setMessage(fieldbookFaceSearchMessage(err));
    } finally {
      setIsFaceSearching(false);
    }
  };

  const clearFaceSearch = () => {
    if (faceSearchPreview?.startsWith('blob:')) URL.revokeObjectURL(faceSearchPreview);
    setFaceSearchPreview('');
    setResults([]);
    setHasFaceSearchResults(false);
    setSelected(null);
    setInteractions([]);
    setRequestId('');
    setHasSearched(false);
    setMessage('');
  };

  const clearSearch = () => {
    if (faceSearchPreview?.startsWith('blob:')) URL.revokeObjectURL(faceSearchPreview);
    setCriteria({});
    setFaceSearchPreview('');
    setHasFaceSearchResults(false);
    setFaceSearchPickerOpen(false);
    setResults([]);
    setSelected(null);
    setInteractions([]);
    setRequestId('');
    setHasSearched(false);
    setInteractionFormOpen(false);
    setInteractionFiles([]);
    setMergePickerOpen(false);
    setMergeSourceRecordId('');
    setMessage('');
  };

  const closeDetail = () => {
    setSelected(null);
    setInteractions([]);
    setInteractionFormOpen(false);
    setInteractionFiles([]);
    setMergePickerOpen(false);
    setMergeSourceRecordId('');
    setEnlargedReferencePhoto(null);
  };

  const deleteInteraction = async (interaction) => {
    if (!selected?.id || !interaction?.id) return;
    const confirmed = window.confirm('Delete your Fieldbook interaction?');
    if (!confirmed) return;
    setDeletingInteractionId(interaction.id);
    try {
      const remove = httpsCallable(functions, 'deleteOrganizationFieldbookInteraction');
      await remove({ type, recordId: selected.id, interactionId: interaction.id });
      await openResult(selected.id);
      setResults((current) => current.map((item) => (
        item.id === selected.id
          ? {
              ...item,
              activeInteractionCount: Math.max((item.activeInteractionCount || 1) - 1, 0),
            }
          : item
      )));
    } catch (err) {
      console.error('Unable to delete Fieldbook interaction:', err);
      setMessage(err?.message || 'Unable to delete this interaction.');
    } finally {
      setDeletingInteractionId('');
    }
  };

  const uploadInteractionFiles = async (files, recordId) => {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error('Sign in required.');
    const uploadedRefs = [];
    try {
      for (const [index, file] of files.entries()) {
        if (!file.type?.startsWith('image/')) {
          throw new Error('Interaction attachments must be images.');
        }
        if (file.size > 10 * 1024 * 1024) {
          throw new Error('Each interaction image must be 10 MB or smaller.');
        }
        const safeName = String(file.name || `interaction-${index}.jpg`)
          .replace(/[^A-Za-z0-9_.-]/g, '_')
          .slice(0, 70);
        const fileRef = ref(
          storage,
          `notes/${uid}/fieldbook_${recordId}_${Date.now()}_${index}_${safeName}`
        );
        await uploadBytes(fileRef, file, { contentType: file.type });
        uploadedRefs.push(fileRef);
      }
      return uploadedRefs.map((fileRef) => fileRef.fullPath);
    } catch (err) {
      await Promise.all(uploadedRefs.map((fileRef) =>
        deleteObject(fileRef).catch(() => null)));
      throw err;
    }
  };

  const addOrganizationInteraction = async (event) => {
    event.preventDefault();
    if (!selected?.id || scope !== 'organization') return;
    if (!interactionDraft.narrative.trim()) {
      setMessage('Add an interaction narrative.');
      return;
    }
    setIsSavingInteraction(true);
    setMessage('');
    let attachmentPaths = [];
    try {
      attachmentPaths = await uploadInteractionFiles(
        interactionFiles,
        selected.id
      );
      const addInteraction = httpsCallable(
        functions,
        'addOrganizationFieldbookInteraction'
      );
      await addInteraction({
        type,
        recordId: selected.id,
        narrative: interactionDraft.narrative,
        attachmentPaths,
      });
      setInteractionDraft({ narrative: '' });
      setInteractionFiles([]);
      setInteractionFormOpen(false);
      setResults((current) => current.map((record) => (
        record.id === selected.id
          ? {
              ...record,
              activeInteractionCount: (record.activeInteractionCount || 0) + 1,
            }
          : record
      )));
      await openResult(selected.id);
    } catch (err) {
      console.error('Unable to add Fieldbook interaction:', err);
      setMessage(err?.message || 'Unable to add this interaction.');
    } finally {
      await Promise.all(attachmentPaths.map((path) =>
        deleteObject(ref(storage, path)).catch(() => null)));
      setIsSavingInteraction(false);
    }
  };

  const selectInteractionFiles = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    const existingKeys = new Set(interactionFiles.map((file) =>
      `${file.name}|${file.size}|${file.lastModified}`));
    const newFiles = selectedFiles.filter((file) => {
      const key = `${file.name}|${file.size}|${file.lastModified}`;
      if (existingKeys.has(key)) return false;
      existingKeys.add(key);
      return true;
    });
    const combinedFiles = [...interactionFiles, ...newFiles];
    if (combinedFiles.length > 3) {
      setMessage('Choose no more than 3 interaction photos.');
    }
    setInteractionFiles(combinedFiles.slice(0, 3));
    event.target.value = '';
  };

  const removeInteractionFile = (fileIndex) => {
    setInteractionFiles((current) =>
      current.filter((_, index) => index !== fileIndex));
  };

  const mergeOrganizationPersonRecords = async () => {
    if (!selected?.id || !mergeSourceRecordId) return;
    const sourceRecord = results.find((record) => record.id === mergeSourceRecordId);
    if (!sourceRecord) return;
    const confirmed = window.confirm(
      `Merge ${sourceRecord.title} into ${selected.title}? ` +
      'The open note will remain and receive all interactions and photos.'
    );
    if (!confirmed) return;

    setIsMerging(true);
    setMessage('');
    try {
      const mergeRecords = httpsCallable(
        functions,
        'mergeOrganizationFieldbookPersonRecords'
      );
      const result = await mergeRecords({
        sourceRecordId: mergeSourceRecordId,
        targetRecordId: selected.id,
      });
      const mergedCount = result.data?.mergedInteractionCount || 0;
      setResults((current) => current
        .filter((record) => record.id !== mergeSourceRecordId)
        .map((record) => record.id === selected.id
          ? {
              ...record,
              activeInteractionCount:
                (record.activeInteractionCount || 0) + mergedCount,
            }
          : record));
      setMergeSourceRecordId('');
      await openResult(selected.id);
    } catch (err) {
      console.error('Unable to merge Fieldbook person records:', err);
      setMessage(err?.message || 'Unable to merge these person records.');
    } finally {
      setIsMerging(false);
    }
  };

  const openOfficerConversation = async (record) => {
    const currentUserId = auth.currentUser?.uid;
    const officerUserId = record?.authorUserId;
    const officerName = [record?.authorRank, record?.authorName]
      .filter(Boolean)
      .join(' ') || 'this officer';
    if (!currentUserId || !officerUserId || currentUserId === officerUserId) return;
    if (!window.confirm(`Open a direct message with ${officerName}?`)) return;

    setLaunchingDmUserId(officerUserId);
    setMessage('');
    try {
      const [currentUserSnap, officerSnap] = await Promise.all([
        getDoc(doc(firestore, 'users', currentUserId)),
        getDoc(doc(firestore, 'users', officerUserId)),
      ]);
      const currentProfile = currentUserSnap.exists() ? currentUserSnap.data() : {};
      const officerProfile = officerSnap.exists() ? officerSnap.data() : {};
      const conversationId = await getOrCreateDirectConversation(
        currentUserId,
        officerUserId,
        {
          memberProfiles: {
            [currentUserId]: {
              firstInitialLastName: currentProfile.firstInitialLastName || null,
              agency: currentProfile.agency || null,
              rank: currentProfile.rank || null,
              avatarUrl: currentProfile.avatarUrl || null,
            },
            [officerUserId]: {
              firstInitialLastName: officerProfile.firstInitialLastName || record.authorName || null,
              agency: officerProfile.agency || record.authorAgency || null,
              rank: officerProfile.rank || record.authorRank || null,
              avatarUrl: officerProfile.avatarUrl || null,
            },
          },
        }
      );
      navigate(`/direct/${conversationId}`, { state: { fromFieldbook: true } });
    } catch (err) {
      console.error('Unable to open officer conversation:', err);
      setMessage('Unable to open this direct-message conversation.');
    } finally {
      setLaunchingDmUserId('');
    }
  };

  const renderRecordFields = (record) => {
    if (!record) return null;
    const rows = record.resultType === 'public_record'
      ? [
          ['Name', [record.firstName, record.middleName, record.lastName, record.suffix].filter(Boolean).join(' ')],
          ['DOB', formatDateOfBirth(record.dob)],
          ['Age', record.age],
          ['Sex', record.sex],
          ['Source Record Date', formatSourceRecordDate(record.recordDate)],
          ['Agency', record.arrestingAgency],
          ['Source', record.provenanceLabel],
        ]
      : type === 'person'
      ? [
          ['Name', [record.firstName, record.middleName, record.lastName].filter(Boolean).join(' ')],
          ['DOB', formatDateOfBirth(record.dob)],
          ['DL', [record.dl, record.dlState].filter(Boolean).join(' / ')],
          ['Phone', record.phone],
          ['Address', [record.streetAddress, record.city, record.addressState, record.addressZip].filter(Boolean).join(', ')],
        ]
      : type === 'vehicle'
        ? [
            ['Vehicle', [record.make, record.model, record.color].filter(Boolean).join(' ')],
            ['Plate', [record.plate, record.plateState].filter(Boolean).join(' / ')],
            ['Owner', record.owner],
          ]
        : [
            ['Address', [record.streetAddress, record.city, record.addressState, record.addressZip].filter(Boolean).join(', ')],
            ['Owner', record.owner],
            ['Occupants', (record.occupants || []).join(', ')],
          ];

    return rows
      .filter(([, value]) => value)
      .map(([label, value]) => (
        <div className="fieldbook-detail-row" key={label}>
          <span>{label}</span>
          <strong>
            {label === 'Source' && record.sourceSearchUrl ? (
              <a
                className="fieldbook-public-source-link"
                href={record.sourceSearchUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {value}
              </a>
            ) : value}
          </strong>
        </div>
      ));
  };

  return (
    <div className="fieldbook-page">
      <header className="fieldbook-header">
        <div className="fieldbook-brand">
          <div className="fieldbook-title-row">
            <span className="fieldbook-target-mark" aria-hidden="true">
              <FontAwesomeIcon icon={faCrosshairs} />
            </span>
            <h1>Fieldbook</h1>
          </div>
          <span className="fieldbook-title-line" aria-hidden="true" />
          <p>Search shared organization records and global officer-authored notes.</p>
        </div>
        <div className="fieldbook-info-button">
          <PageInfoButton title="Fieldbook Guide">
            <section className="fieldbook-guide-warning" aria-labelledby="fieldbook-guide-leads-warning">
              <h4 id="fieldbook-guide-leads-warning">
                INVESTIGATIVE LEADS — NOT POSITIVE IDENTIFICATION
              </h4>
              <p>
                Facial similarity scores, search results, and user-contributed
                records are investigative leads, not proof of identity or
                independently verified facts.
              </p>
              <p>
                <strong className="fieldbook-warning-emphasis">ALWAYS</strong>{' '}
                independently verify information before relying on it.{' '}
                <strong className="fieldbook-warning-emphasis">NEVER</strong>{' '}
                make an arrest, detention, or other enforcement decision based
                solely on BlueWire results.
              </p>
            </section>
            <section className="fieldbook-guide-warning" aria-labelledby="fieldbook-guide-accuracy-warning">
              <h4 id="fieldbook-guide-accuracy-warning">DATA ACCURACY WARNING</h4>
              <p>
                Records may contain inaccurate names, dates, descriptions,
                outdated information, or photographs associated with the wrong
                person.
              </p>
              <p>
                User-contributed information is not necessarily independently
                verified.
              </p>
            </section>
            <p>
              Fieldbook lets verified BlueWire users search records and interactions shared by other members about people, vehicles, and places.
            </p>
            <ul className="page-info-list">
              <li>
                <strong>Organization tab:</strong> Search records contributed by members of your organization and visible only to members of that organization. Organization records are collaborative, allowing members to add their own timestamped interactions to an existing Person, Vehicle, or Place record.
              </li>
              <li>
                <strong>Global tab:</strong> Search Person and Vehicle notes shared with all verified BlueWire users. Global notes are individual, read-only records and cannot be edited or expanded by other users. Because different officers may independently share encounters involving the same person or vehicle, multiple Global records for the same subject are expected and may appear in search results.
              </li>
              <li>
                <strong>Person, Vehicle, and Place tabs:</strong> Choose the record type before
                searching. Place records are available only within the Organization tab.
              </li>
              <li>
                <strong>Structured searches:</strong> Select a supported search method and
                complete every displayed field. Person searches support names, date of birth,
                driver license and state, address, or phone. Vehicle searches use plate and
                state; place searches use address and state.
              </li>
              <li>
                <strong>Face matching:</strong> On the Person tab, take a photo or choose one
                from your gallery to find possible person-record matches. Match scores and
                record details are investigative leads and should be reviewed by the officer;
                they are not a positive identification.
              </li>
              <li>
                <strong>Opening results:</strong> Open a result to review its details, protected
                reference photos, attachments, attribution, and interaction history when
                available.
              </li>
              <li>
                <strong>Contacting contributors:</strong> Tap an eligible officer name and
                confirm to open an existing direct conversation or start one if needed.
              </li>
              <li>
                <strong>Organization tools:</strong> Members can add interactions to an
                organization record. When multiple person results represent the same person,
                the merge control can combine them while preserving their interactions.
              </li>
              <li>
                <strong>Clear Search:</strong> After a completed search, use Clear Search to
                remove the criteria, results, and any selected face-matching image.
              </li>
              <li>
                <strong>Integrity and auditing:</strong> Shared records retain contributor and
                timestamp information. Fieldbook searches and sensitive activity are logged
                for administrative auditing.
              </li>
            </ul>
          </PageInfoButton>
        </div>
      </header>

      {!scope ? <section className="fieldbook-scope-entry" aria-label="Choose a search scope">
        <div className="fieldbook-investigative-warning" role="note">
          <h2>INVESTIGATIVE LEAD — NOT AN IDENTIFICATION</h2>
          <p>
            Search results are possible matches for lead generation only.{' '}
            <strong className="fieldbook-warning-emphasis">ALWAYS</strong>{' '}
            independently verify the information.{' '}
            <strong className="fieldbook-warning-emphasis">NEVER</strong>{' '}
            take enforcement action based solely on these results.
          </p>
          <p>
            These records are entered by humans. Mistakes happen: misspellings,
            incorrect data, and photo-name mismatches are possible.
          </p>
          {!fieldbookAcknowledged && (
            <button
              type="button"
              className="fieldbook-acknowledge-button"
              onClick={() => setFieldbookAcknowledged(true)}
            >
              Acknowledge &amp; Continue
            </button>
          )}
        </div>
        <div className="fieldbook-scope-choices">
          {['organization', 'global'].map((item) => <button type="button" key={item}
            className="fieldbook-scope-choice"
            disabled={!fieldbookAcknowledged}
            onClick={() => handleScopeChange(item)}>
            <FontAwesomeIcon icon={SCOPE_ICONS[item]} aria-hidden="true" />
            <span className="fieldbook-scope-choice-copy">
              <strong>{item === 'organization' ? 'Organization' : 'Global'}</strong>
              <span>{item === 'organization' ? 'Search notes shared within your organization.' :
                'Search notes shared across the BlueWire platform.'}</span>
            </span>
          </button>)}
        </div>
      </section> : <>
      <div className="fieldbook-scope-controls">
        <button type="button" className="fieldbook-change-scope" onClick={returnToScopeSelection}>
          Change scope
        </button>
      </div>
      <div className="fieldbook-tabs" role="tablist" aria-label="Fieldbook scope">
        {['organization', 'global'].map((item) => (
          <button
            type="button"
            key={item}
            role="tab"
            aria-selected={scope === item}
            className={scope === item ? 'active' : ''}
            onClick={() => handleScopeChange(item)}
          >
            <FontAwesomeIcon icon={SCOPE_ICONS[item]} aria-hidden="true" />
            <span>{item === 'organization' ? 'Organization' : 'Global'}</span>
          </button>
        ))}
      </div>

      <div className="fieldbook-type-tabs" role="tablist" aria-label="Fieldbook type">
        {TYPE_OPTIONS.map((item) => {
          const disabled = scope === 'global' && item === 'place';
          return (
            <button
              type="button"
              key={item}
              role="tab"
              aria-selected={type === item}
              className={type === item ? 'active' : ''}
              onClick={() => handleTypeChange(item)}
              disabled={disabled}
            >
              <FontAwesomeIcon icon={TYPE_ICONS[item]} aria-hidden="true" />
              <span>{labelFor(item)}</span>
            </button>
          );
        })}
      </div>

      <form className="fieldbook-search" onSubmit={runSearch}>
        {isPublicStructuredSearch(scope, type, searchMode) && (
          <label className="fieldbook-search-mode">
            <span>State</span>
            <select
              value={publicRecordState}
              disabled={!publicRecordStates.length}
              onChange={(event) => setPublicRecordState(event.target.value)}
            >
              <option value="">All enabled states</option>
              {publicRecordStates.map((state) => (
                <option value={state} key={state}>{stateLabel(state)}</option>
              ))}
            </select>
          </label>
        )}
        <label className="fieldbook-search-mode">
          <span>Search by</span>
          <select
            value={searchMode}
            onChange={(event) => {
              setSearchMode(event.target.value);
              setCriteria({});
            }}
          >
            {SEARCH_OPTIONS[type].map((option) => (
              <option value={option.value} key={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        {scope === 'organization' && type === 'person' && searchMode === 'dlState' && (
          <p className="fieldbook-search-instruction">
            For North Carolina licenses, you may need to include the{' '}
            <strong>4 zeros</strong> at the beginning of the number.
          </p>
        )}
        {scope === 'organization' && isPlatformAdmin && (
          <div className="fieldbook-search-fields">
            <label>
              <span>State</span>
              <select
                value={adminOrganizationState}
                onChange={(event) => {
                  setAdminOrganizationState(event.target.value);
                  setAdminOrganizationId('');
                }}
              >
                <option value="">Select state</option>
                {adminOrganizationStates.map((stateCode) => (
                  <option value={stateCode} key={stateCode}>{stateLabel(stateCode)}</option>
                ))}
              </select>
            </label>
            <label>
              <span>Organization</span>
              <select
                value={adminOrganizationId}
                disabled={!adminOrganizationState}
                onChange={(event) => setAdminOrganizationId(event.target.value)}
              >
                <option value="">Select organization</option>
                {visibleAdminOrganizations.map((organization) => (
                  <option value={organization.id} key={organization.id}>{organization.name}</option>
                ))}
              </select>
            </label>
          </div>
        )}
        <div className="fieldbook-search-fields">
          {SEARCH_OPTIONS[type]
            .find((option) => option.value === searchMode)
            ?.fields.map((field) => (
              <label key={field.name}>
                <span>{field.label}</span>
                {field.control === 'state' ? (
                  <select
                    value={criteria[field.name] || ''}
                    onChange={(event) => setCriteria((current) => ({
                      ...current,
                      [field.name]: event.target.value,
                    }))}
                  >
                    <option value="">Select state</option>
                    {STATES.map((state) => (
                      <option
                        value={state === 'Washington, D.C.' ? 'DC' : state}
                        key={state}
                      >
                        {state}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={field.type || 'text'}
                    value={criteria[field.name] || ''}
                    autoComplete={field.autoComplete || 'off'}
                    maxLength={field.maxLength}
                    placeholder={field.type === 'date'
                      ? undefined
                      : `Enter ${field.label.toLowerCase()}`}
                    onChange={(event) => setCriteria((current) => ({
                      ...current,
                      [field.name]: event.target.value,
                    }))}
                  />
                )}
              </label>
            ))}
        </div>
        <div className={`fieldbook-search-actions${hasSearched ? ' has-clear' : ''}`}>
          <button type="submit" disabled={isSearching}>
            <FontAwesomeIcon icon={faMagnifyingGlass} aria-hidden="true" />
            <span>{isSearching ? 'Searching...' : `Search ${labelFor(type)}`}</span>
          </button>
          {hasSearched && (
            <button
              type="button"
              className="fieldbook-clear-search"
              onClick={clearSearch}
              disabled={isSearching || isFaceSearching}
            >
              <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
              <span>Clear Search</span>
            </button>
          )}
        </div>
      </form>

      {type === 'person' && (
        <div className="fieldbook-face-search">
          {!faceSearchPickerOpen ? (
            <button
              type="button"
              className="fieldbook-face-search-main"
              disabled={isFaceSearching}
              onClick={() => setFaceSearchPickerOpen(true)}
            >
              <span className="fieldbook-face-focus-icon" aria-hidden="true">
                <FontAwesomeIcon className="fieldbook-face-focus-corners" icon={faExpand} />
                <FontAwesomeIcon className="fieldbook-face-focus-person" icon={faUser} />
              </span>
              <span className="fieldbook-face-search-copy">
                <strong>{isFaceSearching ? 'Searching...' : 'Face Search'}</strong>
                {!isFaceSearching && (
                  <span>Search using facial matching.</span>
                )}
              </span>
              <FontAwesomeIcon
                className="fieldbook-face-search-arrow"
                icon={faChevronRight}
                aria-hidden="true"
              />
            </button>
          ) : (
            <div className="fieldbook-face-picker">
              <label>
                Take Photo
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  hidden
                  disabled={isFaceSearching}
                  onChange={handleFaceSearchSelect}
                />
              </label>
              <label>
                Choose from Gallery
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  disabled={isFaceSearching}
                  onChange={handleFaceSearchSelect}
                />
              </label>
              <button type="button" onClick={() => setFaceSearchPickerOpen(false)}>
                Cancel
              </button>
            </div>
          )}
          {faceSearchPreview && (
            <div className="fieldbook-face-preview">
              <img src={faceSearchPreview} alt="Face search reference" />
              <button type="button" onClick={clearFaceSearch} disabled={isFaceSearching}>
                Clear Face Search
              </button>
            </div>
          )}
        </div>
      )}

      {message && <div className="fieldbook-message">{message}</div>}

      <section className="fieldbook-results" aria-label="Fieldbook search results">
        {results.map((record) => {
          const age = record.age || calculateAge(record.dob);
          const officerLabel = [record.authorRank, record.authorName]
            .filter(Boolean)
            .join(' ') || 'Unknown officer';
          const isCurrentOfficer = record.authorUserId === auth.currentUser?.uid;
          return (
            <article
              key={record.id}
              className={`fieldbook-result${selected?.id === record.id ? ' selected' : ''}`}
            >
              <button
                type="button"
                className="fieldbook-result-open"
                onClick={() => openResult(record.id)}
                disabled={Boolean(isOpening)}
              >
                {record.type === 'person' && (
                  <span className="fieldbook-result-photo-wrap">
                    {record.referencePhoto?.signedUrl ? (
                      <img
                        className="fieldbook-result-photo"
                        src={record.referencePhoto.signedUrl}
                        alt="Profile face"
                      />
                    ) : (
                      <span className="fieldbook-result-photo placeholder">No Photo</span>
                    )}
                  </span>
                )}
                <span className="fieldbook-result-summary">
                  <span className="fieldbook-result-title">{record.title}</span>
                  {record.type === 'person' && (
                    <>
                      <span>DOB: {formatDateOfBirth(record.dob) || 'N/A'} | State: {record.state || 'N/A'}</span>
                      <span>Age: {age === '' ? 'N/A' : age}</span>
                      {record.resultType === 'public_record' && (
                        <span>Source Record Date: {formatSourceRecordDate(record.sourceRecordDate) || 'N/A'}</span>
                      )}
                    </>
                  )}
                  <span className="fieldbook-result-meta">
                    {record.resultType === 'public_record'
                      ? 'Public record'
                      : scope === 'organization'
                      ? `${record.activeInteractionCount || 0} active interactions`
                      : 'Global shared note'}
                    {record.resultType === 'public_record'
                      ? record.score !== undefined && record.score !== null
                        ? ` | ${formatPublicFaceSimilarity(record.score)}`
                        : ''
                      : record.score ? ` | ${record.score}% face match` : ''}
                  </span>
                </span>
              </button>
              <div className="fieldbook-result-attribution">
                <span>
                  <strong>{record.resultType === 'public_record' ? 'Source:' : 'Sharing agency:'}</strong>{' '}
                  {record.resultType === 'public_record' && record.sourceSearchUrl ? (
                    <a
                      className="fieldbook-public-source-link"
                      href={record.sourceSearchUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {record.authorAgency || record.organizationName || 'Unknown agency'}
                    </a>
                  ) : (
                    record.authorAgency || record.organizationName || 'Unknown agency'
                  )}
                </span>
                {record.resultType !== 'public_record' && <span>
                  <strong>Entered by:</strong>{' '}
                  {record.authorUserId && !isCurrentOfficer ? (
                    <button
                      type="button"
                      className="fieldbook-officer-link"
                      disabled={launchingDmUserId === record.authorUserId}
                      onClick={() => openOfficerConversation(record)}
                    >
                      {launchingDmUserId === record.authorUserId ? 'Opening...' : officerLabel}
                    </button>
                  ) : (
                    <span>{officerLabel}{isCurrentOfficer ? ' (You)' : ''}</span>
                  )}
                </span>}
                {(record.resultType === 'public_record' || hasFaceSearchResults) && (
                  <span className="fieldbook-face-warning" role="note">
                    <FontAwesomeIcon icon={faTriangleExclamation} aria-hidden="true" />
                    <span>POTENTIAL FACIAL MATCH — VERIFY IDENTITY INDEPENDENTLY!</span>
                  </span>
                )}
              </div>
            </article>
          );
        })}
      </section>

      {selected && (
        <div
          className="fieldbook-detail-modal"
          role="presentation"
          onClick={closeDetail}
        >
        <section
          className="fieldbook-detail"
          role="dialog"
          aria-modal="true"
          aria-label={`${selected.title} Fieldbook record`}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="fieldbook-detail-header">
            <div className="fieldbook-detail-identity">
              {selected.type === 'person' && (
                selected.referencePhotos?.[0]?.signedUrl ? (
                  <button
                    type="button"
                    className="fieldbook-detail-profile-photo"
                    aria-label="Enlarge profile photo"
                    title="Enlarge profile photo"
                    onClick={() => setEnlargedReferencePhoto({
                      ...selected.referencePhotos[0],
                      label: 'Photo',
                    })}
                  >
                    <img
                      src={selected.referencePhotos[0].signedUrl}
                      alt="Profile face"
                      onError={selected.resultType === 'public_record'
                        ? renewPublicReferencePhoto
                        : undefined}
                    />
                  </button>
                ) : (
                  <div className="fieldbook-detail-profile-photo placeholder">No Photo</div>
                )
              )}
              <div>
                <h2>{selected.title}</h2>
                <p>
                  {selected.resultType === 'public_record' ? 'Source' : 'Sharing agency'}: {' '}
                  {selected.resultType === 'public_record' && selected.sourceSearchUrl ? (
                    <a
                      className="fieldbook-public-source-link"
                      href={selected.sourceSearchUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {selected.createdByAgency || selected.authorAgency || selected.organizationName || 'Unknown agency'}
                    </a>
                  ) : (
                    selected.createdByAgency || selected.authorAgency || selected.organizationName || 'Unknown agency'
                  )}
                </p>
                {(selected.resultType === 'public_record' || hasFaceSearchResults) && (
                  <>
                    <p className="fieldbook-face-warning" role="note">
                      <FontAwesomeIcon icon={faTriangleExclamation} aria-hidden="true" />
                      <span>POTENTIAL FACIAL MATCH — VERIFY IDENTITY INDEPENDENTLY!</span>
                    </p>
                    {selected.score !== undefined && selected.score !== null && (
                      <p>Face match: {formatPublicFaceSimilarity(selected.score)}</p>
                    )}
                  </>
                )}
              </div>
            </div>
            <button
              type="button"
              className="fieldbook-detail-close"
              onClick={closeDetail}
            >
              Close
            </button>
          </div>

          {message && <div className="fieldbook-message">{message}</div>}

          {scope === 'organization' && type === 'person' && results.length > 1 && (
            <>
              <div className="fieldbook-detail-actions merge-only">
                <button
                  type="button"
                  onClick={() => {
                    setMergePickerOpen((current) => !current);
                    setInteractionFormOpen(false);
                  }}
                >
                  {mergePickerOpen ? 'Cancel Merge' : 'Merge Person Notes'}
                </button>
              </div>

              {mergePickerOpen && (
                <div className="fieldbook-merge-form">
                  <label>
                    <span>Select which result to merge into this note</span>
                    <select
                      value={mergeSourceRecordId}
                      onChange={(event) => setMergeSourceRecordId(event.target.value)}
                    >
                      <option value="">Select another person record</option>
                      {results
                        .filter((record) =>
                          record.id !== selected.id && record.type === 'person')
                        .map((record) => (
                          <option value={record.id} key={record.id}>
                            {record.title} | DOB: {formatDateOfBirth(record.dob) || 'N/A'}
                          </option>
                        ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    disabled={!mergeSourceRecordId || isMerging}
                    onClick={mergeOrganizationPersonRecords}
                  >
                    {isMerging ? 'Merging...' : 'Merge Into Open Note'}
                  </button>
                </div>
              )}
            </>
          )}

          <div className="fieldbook-detail-grid">
            {renderRecordFields(selected)}
          </div>

          {selected.referencePhotos?.length > 1 && (
            <div className="fieldbook-reference-photos">
              {selected.referencePhotos.slice(1).map((photo, index) => (
                <button
                  type="button"
                  key={photo.path || index}
                  className="fieldbook-reference-photo"
                  onClick={() => setEnlargedReferencePhoto({
                    ...photo,
                    label: `Reference photo ${index + 2}`,
                  })}
                >
                  <img
                    src={photo.signedUrl}
                    alt={`Reference profile ${index + 2}`}
                  />
                  <span>Enlarge</span>
                </button>
              ))}
            </div>
          )}

          {scope === 'global' && !selected.globalInteractions?.length && selected.narrative && (
            <div className="fieldbook-narrative">
              <span>Narrative</span>
              <p>{selected.narrative}</p>
            </div>
          )}

          {scope === 'global' && selected.attachments?.length > 0 && (
            <div className="fieldbook-photos">
              {selected.attachments.map((photo, index) => (
                <a key={photo.path || index} href={photo.signedUrl} target="_blank" rel="noreferrer">
                  View attachment {index + 1}
                </a>
              ))}
            </div>
          )}

          {scope === 'organization' && (
            <div className="fieldbook-interactions">
              <div className="fieldbook-interactions-header">
                <h3>Interactions</h3>
                <button
                  type="button"
                  className="fieldbook-add-interaction"
                  onClick={() => {
                    setInteractionFormOpen((current) => !current);
                    setMergePickerOpen(false);
                  }}
                >
                  {interactionFormOpen ? 'Cancel' : 'Add New Interaction'}
                </button>
              </div>
              {interactionFormOpen && (
                <form
                  className="fieldbook-interaction-form"
                  onSubmit={addOrganizationInteraction}
                >
                  <label>
                    <span>Narrative</span>
                    <textarea
                      required
                      rows="5"
                      maxLength="12000"
                      value={interactionDraft.narrative}
                      onChange={(event) => setInteractionDraft((current) => ({
                        ...current,
                        narrative: event.target.value,
                      }))}
                    />
                  </label>
                  <label className="fieldbook-interaction-upload">
                    <span>Photos (up to 3)</span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={selectInteractionFiles}
                    />
                  </label>
                  {interactionFiles.length > 0 && (
                    <div className="fieldbook-selected-files">
                      {interactionFiles.map((file, index) => (
                        <InteractionImagePreview
                          key={`${file.name}-${file.size}-${file.lastModified}`}
                          file={file}
                          onRemove={() => removeInteractionFile(index)}
                        />
                      ))}
                    </div>
                  )}
                  <button type="submit" disabled={isSavingInteraction}>
                    {isSavingInteraction ? 'Saving...' : 'Save Interaction'}
                  </button>
                </form>
              )}
              {interactions.map((interaction) => {
                const officerRecord = {
                  authorUserId: interaction.createdByUserId,
                  authorName: interaction.createdByName,
                  authorRank: interaction.createdByRank,
                  authorAgency: interaction.createdByAgency,
                };
                const officerLabel = [
                  interaction.createdByRank,
                  interaction.createdByName,
                ].filter(Boolean).join(' ') || 'Unknown officer';
                const isCurrentOfficer =
                  interaction.createdByUserId === auth.currentUser?.uid;

                return (
                  <article key={interaction.id} className="fieldbook-interaction">
                    <div className="fieldbook-interaction-meta">
                      <div>
                        {interaction.createdByUserId && !isCurrentOfficer ? (
                          <button
                            type="button"
                            className="fieldbook-officer-link"
                            disabled={launchingDmUserId === interaction.createdByUserId}
                            onClick={() => openOfficerConversation(officerRecord)}
                          >
                            {launchingDmUserId === interaction.createdByUserId
                              ? 'Opening...'
                              : officerLabel}
                          </button>
                        ) : (
                          <strong>
                            {officerLabel}{isCurrentOfficer ? ' (You)' : ''}
                          </strong>
                        )}
                        {interaction.createdByAgency && (
                          <span>Agency: {interaction.createdByAgency}</span>
                        )}
                      </div>
                      <span>
                        {formatDate(interaction.interactionAt || interaction.createdAt) ||
                          'Date unavailable'}
                      </span>
                    </div>
                    {interaction.narrative && <p>{interaction.narrative}</p>}
                    {interaction.attachments?.length > 0 && (
                      <div className="fieldbook-photos">
                        {interaction.attachments.map((photo, index) => (
                          <a key={photo.path || index} href={photo.signedUrl} target="_blank" rel="noreferrer">
                            View attachment {index + 1}
                          </a>
                        ))}
                      </div>
                    )}
                    {isCurrentOfficer && (
                      <button
                        type="button"
                        className="fieldbook-delete-interaction"
                        disabled={deletingInteractionId === interaction.id}
                        onClick={() => deleteInteraction(interaction)}
                      >
                        {deletingInteractionId === interaction.id ? 'Deleting...' : 'Delete My Interaction'}
                      </button>
                    )}
                  </article>
                );
              })}
            </div>
          )}

          {scope === 'global' && selected.globalInteractions?.length > 0 && (
            <div className="fieldbook-interactions">
              <h3>Interactions</h3>
              {selected.globalInteractions.map((interaction) => (
                <article key={interaction.id} className="fieldbook-interaction">
                  <div className="fieldbook-interaction-meta">
                    <div>
                      <strong>
                        {[interaction.createdByRank, interaction.createdByName]
                          .filter(Boolean)
                          .join(' ')}
                      </strong>
                      {interaction.createdByAgency && (
                        <span>Agency: {interaction.createdByAgency}</span>
                      )}
                    </div>
                    <span>
                      {formatDate(interaction.interactionAt || interaction.createdAt) ||
                        'Date unavailable'}
                    </span>
                  </div>
                  {interaction.narrative && <p>{interaction.narrative}</p>}
                  {interaction.attachments?.length > 0 && (
                    <div className="fieldbook-photos">
                      {interaction.attachments.map((photo, index) => (
                        <a
                          key={photo.path || index}
                          href={photo.signedUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          View attachment {index + 1}
                        </a>
                      ))}
                    </div>
                  )}
                </article>
              ))}
            </div>
          )}
        </section>
        </div>
      )}

      {enlargedReferencePhoto && (
        <div
          className="fieldbook-photo-modal"
          role="dialog"
          aria-modal="true"
          aria-label={enlargedReferencePhoto.label}
          onClick={() => setEnlargedReferencePhoto(null)}
        >
          <div
            className="fieldbook-photo-modal-content"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="fieldbook-photo-modal-header">
              <strong>{enlargedReferencePhoto.label}</strong>
              <button
                type="button"
                onClick={() => setEnlargedReferencePhoto(null)}
              >
                Close
              </button>
            </div>
            <img
              src={enlargedReferencePhoto.signedUrl}
              alt={enlargedReferencePhoto.label}
            />
          </div>
        </div>
      )}
      </>}
    </div>
  );
}
